import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useModal } from '../../../contexts/ModalContext';

const HeroSection = () => {
  const { openConsultation } = useModal();
  const navigate = useNavigate();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [typewriterText, setTypewriterText] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const heroRef = useRef(null);
  const isInView = useInView(heroRef, { once: true });
  const controls = useAnimation();

  const typewriterPhrases = [
    "Custom Software Development",
    "Tailored Web Applications",
    "High-Performance Mobile Apps",
    "Lightweight Business Automation",
    "Honest Code, Direct Collaboration"
  ];
  const tagline = "Simple, modern software and automations for growing businesses.";

  // Floating particles data
  const particles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 20 + 10,
    delay: Math.random() * 2
  }));

  // Geometric shapes data
  const geometricShapes = [
    { type: 'cube', x: 20, y: 30, size: 60, rotation: 0 },
    { type: 'sphere', x: 80, y: 20, size: 40, rotation: 45 },
    { type: 'pyramid', x: 70, y: 70, size: 50, rotation: 90 },
    { type: 'torus', x: 15, y: 80, size: 35, rotation: 135 }
  ];

  useEffect(() => {
    const handleMouseMove = (e) => {
      const rect = heroRef.current?.getBoundingClientRect();
      if (rect) {
        setMousePosition({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100
        });
      }
    };

    const heroElement = heroRef.current;
    if (heroElement) {
      heroElement.addEventListener('mousemove', handleMouseMove);
      return () => heroElement.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  useEffect(() => {
    if (isInView) {
      controls.start('visible');

      let index = 0;
      let phraseIndex = 0;
      let isDeleting = false;
      let timeoutId;

      const type = () => {
        const currentText = typewriterPhrases[phraseIndex];

        if (isDeleting) {
          setTypewriterText(currentText.substring(0, index));
          index--;
        } else {
          setTypewriterText(currentText.substring(0, index));
          index++;
        }

        let typeSpeed = 100;

        if (isDeleting) {
          typeSpeed /= 2; // Delete faster
        }

        if (!isDeleting && index === currentText.length + 1) {
          // Finished typing, pause before deleting
          typeSpeed = 2000;
          isDeleting = true;
        } else if (isDeleting && index === 0) {
          isDeleting = false;
          phraseIndex = (phraseIndex + 1) % typewriterPhrases.length;
          typeSpeed = 500;
        }

        timeoutId = setTimeout(type, typeSpeed);
      };

      type();

      return () => clearTimeout(timeoutId);
    }
  }, [isInView, controls]);

  // Scroll detection to hide scroll indicator
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen bg-gradient-to-br from-background via-muted/30 to-primary/5 overflow-hidden flex items-center justify-center"
    >
      {/* Animated Background Grid */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(6,182,212,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(6,182,212,0.1)_1px,transparent_1px)] bg-[size:50px_50px] animate-pulse-slow"></div>
      </div>

      {/* Floating Particles */}
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute w-2 h-2 bg-primary/30 rounded-full"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`
          }}
          animate={{
            x: [0, mousePosition.x * 0.1, 0],
            y: [0, mousePosition.y * 0.1, 0],
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.7, 0.3]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            delay: particle.delay,
            ease: "easeInOut"
          }}
        />
      ))}

      {/* 3D Geometric Shapes */}
      {geometricShapes.map((shape, index) => (
        <motion.div
          key={index}
          className="absolute"
          style={{
            left: `${shape.x}%`,
            top: `${shape.y}%`,
            width: `${shape.size}px`,
            height: `${shape.size}px`
          }}
          animate={{
            rotateX: [shape.rotation, shape.rotation + 360],
            rotateY: [0, 360],
            x: [0, mousePosition.x * 0.05, 0],
            y: [0, mousePosition.y * 0.05, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          <div className={`w-full h-full bg-gradient-to-br from-primary/20 to-accent/20 backdrop-blur-sm border border-primary/30 ${shape.type === 'cube' ? 'rounded-lg' :
            shape.type === 'sphere' ? 'rounded-full' :
              shape.type === 'pyramid' ? 'rounded-t-full' : 'rounded-full'
            } shadow-brand`}></div>
        </motion.div>
      ))}

      {/* Main Content */}
      <motion.div
        className="relative z-10 text-center px-6 max-w-6xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate={controls}
      >
        {/* Hero Headline with Typewriter Effect */}
        <motion.div variants={itemVariants} className="mb-6 min-h-[200px] md:min-h-[240px] lg:min-h-[280px] flex flex-col items-center justify-center">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground mb-4">
            <span className="block text-2xl md:text-3xl font-semibold text-text-secondary mb-3 tracking-wide">
              Global Software Development Partner
            </span>
            <span className="relative inline-block min-h-[60px] md:min-h-[80px]">
              <span className="text-gradient">{typewriterText}</span>
              <motion.span
                className="inline-block w-1 h-12 md:h-16 lg:h-20 bg-primary ml-2 align-middle"
                animate={{ opacity: [1, 0, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              />
            </span>
          </h1>
          <motion.p
            className="text-xl md:text-2xl text-text-secondary font-medium"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3, duration: 0.8 }}
          >
            {tagline}
          </motion.p>
          <motion.p
            className="text-sm md:text-md text-text-secondary mt-4 max-w-2xl mx-auto opacity-70 leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 3.2, duration: 0.8 }}
          >
            We help clinics, hotels, local shops, and service companies optimize their operations 
            with custom billing, booking calendars, WhatsApp alerts, and map listings. Simple tools that just work.
          </motion.p>
        </motion.div>

        {/* Value Proposition Cards */}
        <motion.div
          variants={itemVariants}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 w-full max-w-4xl mx-auto"
        >
          {[
            { icon: 'Code', title: 'Custom Software', desc: 'Invoicing, ERP, CRM, and booking' },
            { icon: 'Bot', title: 'WhatsApp & AI Automation', desc: 'Instant confirmations & notifications' },
            { icon: 'Globe', title: 'Websites & Local SEO', desc: 'Rank high on Google Maps and search' }
          ].map((item, index) => (
            <motion.div
              key={index}
              className="bg-card/80 backdrop-blur-sm border border-border rounded-xl p-6 shadow-brand hover:shadow-brand-lg transition-all duration-300"
              whileHover={{ scale: 1.05, y: -5 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 3.5 + index * 0.2 }}
            >
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center mb-4 mx-auto">
                <Icon name={item.icon} size={24} color="white" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
              <p className="text-text-secondary text-sm">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <Button
            variant="default"
            size="lg"
            iconName="Rocket"
            iconPosition="left"
            className="bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 shadow-brand hover:shadow-brand-lg transform hover:scale-105 transition-all duration-300"
            onClick={openConsultation}
          >
            Start Your Project
          </Button>
          <Button
            variant="outline"
            size="lg"
            iconName="Code"
            iconPosition="left"
            className="border-primary/30 text-primary hover:bg-primary/5 hover:border-primary/50 transform hover:scale-105 transition-all duration-300"
            onClick={() => navigate('/services')}
          >
            Explore Services
          </Button>
        </motion.div>

        {/* Scroll Indicator - Hidden when scrolled */}
        {!scrolled && (
          <motion.div
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <motion.div
              className="flex flex-col items-center text-text-secondary cursor-pointer hover:text-primary transition-colors duration-300"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              onClick={() => {
                window.scrollBy({
                  top: window.innerHeight,
                  behavior: 'smooth'
                });
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="text-sm mb-2">Scroll to explore</span>
              <Icon name="ChevronDown" size={20} />
            </motion.div>
          </motion.div>
        )}
      </motion.div>

      {/* Experience Badge */}
      <motion.div
        className="hidden md:block absolute top-20 right-6 bg-card/90 backdrop-blur-sm border border-border rounded-lg p-3 shadow-brand"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 2, duration: 0.8 }}
      >
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
          <span className="text-xs text-text-secondary">Direct Cooperation</span>
        </div>
        <div className="text-sm font-bold text-foreground">Zero Bloat, Fair Prices</div>
      </motion.div>
    </section>
  );
};

export default HeroSection;